import React from 'react';
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Close } from '@mui/icons-material';

interface ProductReelsViewerProps<T> {
  open: boolean;
  onClose: () => void;
  items: T[];
  initialIndex?: number;
  renderItem: (item: T, index: number) => React.ReactNode;
  title?: string;
}

const ProductReelsViewer = <T,>({
  open,
  onClose,
  items,
  initialIndex = 0,
  renderItem,
  title,
}: ProductReelsViewerProps<T>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const idx = Math.max(0, Math.min(initialIndex, Math.max(0, items.length - 1)));
    const el = scrollerRef.current?.querySelector(`[data-reel-index="${idx}"]`) as HTMLElement | null;
    if (el) {
      el.scrollIntoView({ block: 'start' });
    }
  }, [open, initialIndex, items.length]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0,0,0,0.92)',
        },
      }}
    >
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 5,
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ pointerEvents: 'auto' }}>
          {title ? (
            <Typography sx={{ color: 'white', fontWeight: 900, letterSpacing: 0.3 }}>
              {title}
            </Typography>
          ) : (
            <Box />
          )}
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{
            pointerEvents: 'auto',
            color: 'white',
            bgcolor: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.18)',
            backdropFilter: 'blur(10px)',
            width: isMobile ? 52 : undefined,
            height: isMobile ? 52 : undefined,
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.22)',
            },
          }}
        >
          <Close sx={{ fontSize: isMobile ? 30 : undefined }} />
        </IconButton>
      </Box>

      <Box
        ref={scrollerRef}
        sx={{
          height: '100vh',
          overflowY: 'auto',
          scrollSnapType: 'y mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {items.map((item, index) => (
          <Box
            key={index}
            data-reel-index={index}
            sx={{
              height: '100vh',
              scrollSnapAlign: 'start',
              scrollSnapStop: 'always',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: { xs: 1.5, sm: 2.5 },
              pt: 8,
              pb: 4,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 520,
                transform: { xs: 'scale(1.0)', sm: 'scale(1.02)' },
                transformOrigin: 'center',
              }}
            >
              {renderItem(item, index)}
            </Box>
          </Box>
        ))}

        {items.length === 0 && (
          <Box
            sx={{
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography sx={{ opacity: 0.9, fontWeight: 700 }}>
              No items to show
            </Typography>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ProductReelsViewer;
